import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { ZERO_ADDRESS } from '@blockcatio/tabby-library/lib/constants';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';
import { PropTypeBn } from '@blockcatio/tabby-library/lib/utilities/PropTypeBn';
import FormattedWei from '@blockcatio/tabby-library/lib/components/FormattedWei';

import ToolTip from './ToolTip';
import { GAS_GUIDE_URL } from '../constants';

// Display the breakdown of the transaction costs.
const CostCalculator = ({
  amount,
  tokenAddress,
  paymentType,
  payWithEthFee,
  payWithCatFee,
  formatPaymentAmount,
  canPayFee,
  gasFee,
  gasForward,
}) => {
  const paymentFee = paymentType === 'ETH' ? payWithEthFee : payWithCatFee;
  // Don't display the fee error if no amount is entered.
  const displayFeeError = !amount.eq(safeToBn(0));

  let totalGasFee = gasFee || safeToBn(0);
  if (gasForward) {
    totalGasFee = totalGasFee.add(gasForward);
  }
  const gasFeeNote =
    gasForward && gasForward.gt(safeToBn(0)) ? (
      <React.Fragment>
        <p>
          Note:* Because the address you are sending to has insufficient funds to accept the
          payment, we&apos;re adding an additional{' '}
          <strong>
            <FormattedWei wei={gasForward} decimals={7} />
          </strong>{' '}
          in gas so the recipient can claim the payment.
        </p>
        <p>
          <strong>
            <FormattedWei wei={gasFee} decimals={7} /> +{' '}
            <FormattedWei wei={gasForward} decimals={7} /> ={' '}
            <FormattedWei wei={totalGasFee} decimals={7} />
          </strong>
        </p>
      </React.Fragment>
    ) : null;

  return (
    <table className={`payment-summary${!canPayFee && displayFeeError ? ' invalid' : ''}`}>
      <tbody>
        <tr className="payment">
          <td className="label">Payment</td>
          <td className="amount">{formatPaymentAmount(amount, tokenAddress)}</td>
        </tr>
        <tr className="fee">
          <td className="label">Fee</td>
          <td className="amount">
            <FormattedWei wei={paymentFee} suffix={paymentType} />
          </td>
        </tr>
        {displayFeeError &&
          !canPayFee && (
            <tr className="error-message">
              <td colSpan="2">Insufficient funds</td>
            </tr>
          )}
        <tr className="gas">
          <td className="label">
            Gas
            <ToolTip
              bottom="152%"
              content={[
                <div key="tooltip">
                  Gas is the fee used to pay for the{' '}
                  <a href={GAS_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                    Ethereum network
                  </a>.{gasFeeNote}
                </div>,
              ]}
            >
              <img src="/images/info-bubble.png" alt="info" />
            </ToolTip>
          </td>
          <td className="amount">
            <FormattedWei wei={totalGasFee} />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

CostCalculator.defaultProps = {
  amount: null,
  tokenAddress: ZERO_ADDRESS,
  paymentType: '',
  payWithEthFee: null,
  payWithCatFee: null,
  formatPaymentAmount: noop,
  canPayFee: true,
  gasFee: null,
  gasForward: null,
};

CostCalculator.propTypes = {
  amount: PropTypeBn,
  tokenAddress: PropTypes.string,
  paymentType: PropTypes.string,
  payWithEthFee: PropTypeBn,
  payWithCatFee: PropTypeBn,
  formatPaymentAmount: PropTypes.func,
  canPayFee: PropTypes.bool,
  gasFee: PropTypeBn,
  gasForward: PropTypeBn,
};

export default CostCalculator;



// WEBPACK FOOTER //
// ./src/components/CostCalculator.js